import { policyName } from '@/features/decisions/config/personaContext';
import type { InventoryCoveragePolicy } from '@/features/decisions/decisions.api.types';
import { fmtCurrencyPerUnit, fmtServiceLevel, fmtUnits } from '@/features/decisions/utils/historicalPerformanceFormat';

interface Props {
  // The selected policy whose params are shown.
  policy: InventoryCoveragePolicy | undefined;
  // Policy picker: when provided, a dropdown in the header selects the policy
  // (drives the reorder editor + charts). Lists the Keystone comparison policies.
  policies?: InventoryCoveragePolicy[];
  selectedExperimentId?: string;
  activeExperimentId?: string | null;
  onSelect?: (experimentId: string) => void;
}

// Policy parameters in effect for the selected window, with a dropdown to switch
// the active policy. Fields per the TR-OR-UX-01 spec.
export function PolicyDetailsPanel({ policy, policies, selectedExperimentId, activeExperimentId, onSelect }: Props) {
  const rows: Array<{ label: string; value: string; source: string }> = policy
    ? [
        {
          label: 'Target Service Level',
          value: fmtServiceLevel(policy.model_target_service_level),
          source: 'model_target_service_level',
        },
        {
          label: 'Periods Between Replenishment',
          value: policy.periods_between_replenishment == null ? '—' : String(policy.periods_between_replenishment),
          source: 'periods_between_replenishment',
        },
        {
          label: 'Total Lead Time',
          value: policy.total_lead_time == null ? '—' : `${policy.total_lead_time} wk`,
          source: 'total_lead_time',
        },
        { label: 'Minimum Order Size', value: fmtUnits(policy.min_order_size), source: 'min_order_size' },
        { label: 'Net Revenue / Unit', value: fmtCurrencyPerUnit(policy.net_rev_per_unit), source: 'net_rev_per_unit' },
        { label: 'Cost / Unit', value: fmtCurrencyPerUnit(policy.cost_per_unit), source: 'cost_per_unit' },
      ]
    : [];

  const showPicker = policies && policies.length > 0 && onSelect && selectedExperimentId;

  return (
    <div>
      <div className="flex items-center justify-between gap-2">
        <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500">Policy Details</h3>
        {showPicker ? (
          <select
            value={selectedExperimentId}
            onChange={(e) => onSelect(e.target.value)}
            aria-label="Select policy"
            className="max-w-full rounded-md border border-gray-300 bg-white px-2 py-1 text-xs font-medium text-gray-700 focus:border-[#274483] focus:outline-none focus:ring-1 focus:ring-[#274483]"
          >
            {policies.map((p) => (
              <option key={p.experiment_id} value={p.experiment_id}>
                {policyName(p.experiment_id)}
                {p.experiment_id === activeExperimentId ? ' · Active' : ''}
              </option>
            ))}
          </select>
        ) : (
          policy && <span className="text-xs text-gray-500">{policyName(policy.experiment_id)}</span>
        )}
      </div>

      {policy ? (
        <dl className="mt-2 grid grid-flow-col grid-rows-3 gap-x-6 text-sm">
          {rows.map((r) => (
            <div
              key={r.label}
              className="flex items-baseline justify-between gap-2 border-b border-gray-100 py-2"
              title={r.source}
            >
              <dt className="text-gray-700">{r.label}</dt>
              <dd className="font-semibold tabular-nums text-gray-900">{r.value}</dd>
            </div>
          ))}
        </dl>
      ) : (
        <div className="mt-2 text-sm text-gray-500">Select a policy to see its details.</div>
      )}
    </div>
  );
}
